// @flow
import * as React from 'react';
import { Box, Button, MenuItem, Typography } from '@mui/material';
import { DrugInfoWithPackagesFlags } from '@coverright/data-access/types/medicare';
import { Pill } from '@phosphor-icons/react';
import { DoctorsDrugsItemDescription } from '../doctors-drugs-item';
import { SelectInput } from '@coverright/ui/inputs';
import { useMemo, useState } from 'react';

type Props = {
  info?: DrugInfoWithPackagesFlags | null;
  onClose: (switchTo?: { name: string; rxcui: string }) => void;
};

export function DrugSelectionModalNoPackages(props: Props) {
  const [brandedRxcui, setBrandedRxcui] = useState((props.info?.brandedDetails[0]?.rxcui as string) || '');

  const hasPackages =
    props.info?.hasBrandedPackages || props.info?.hasGenericPackages;

  const brandedDetails = props.info?.brandedDetails || [];
  const isGeneric = props.info?.isGeneric;

  const switchTo = useMemo(() => {
    if (isGeneric) {
      if (brandedDetails.length === 1) {
        return {
          rxcui: brandedDetails[0].rxcui as string,
          name: brandedDetails[0].name,
        };
      } else {
        const drug = brandedDetails.find(
          (d) => d.rxcui === brandedRxcui
        );
        if (drug) {
          return { rxcui: drug.rxcui, name: drug.name };
        } else {
          return undefined
        }
      }
    }
    return {
      rxcui: props.info?.genericRxcui as string,
      name: props.info?.genericName as string,
    }
  }, [isGeneric, brandedDetails, brandedRxcui]);

  if (!props.info) {
    return null;
  }

  const handleSwitchClick = () => {
    props.onClose(switchTo);
  }

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
      <DoctorsDrugsItemDescription icon={<Pill size={24} color={'#216069'} />}
                                   title={props.info.name || ''} />
      <Typography>isn’t currently available under any Medicare Advantage plan offered by CoverRight.</Typography>

      {hasPackages && <>
        {brandedDetails.length <= 1 && <>
          <Typography>
            It has a lower cost {isGeneric ? 'branded' : 'generic'} version called:
          </Typography>
          <DoctorsDrugsItemDescription icon={<Pill size={24} color={'#216069'} />}
                                       title={(isGeneric ? brandedDetails[0].name : props.info.genericName) || ''} />
        </>}

        {brandedDetails.length > 1 && (
          <BrandedDrugSelect items={brandedDetails} value={brandedRxcui} onChange={setBrandedRxcui} />
        )}

        <Typography variant={'h3'}>
          Would you like to add the{' '}
          {isGeneric ? 'branded' : 'generic'} version to your
          list instead?
        </Typography>
      </>}

      {!hasPackages && (
        <Typography variant={'h3'} sx={{my: 2}}>
          Speak to one of our licensed insurance agents if this is important to you.
        </Typography>
      )}


      <Box sx={{display: 'flex',  flex: 1, gap: 1.5, flexDirection: {xs: 'column', sm: 'row'}, alignItems: { sm: 'center' }, justifyContent: 'flex-end'}}>
        <Button size={'large'} variant={'outlined'} onClick={() => props.onClose()}>Back to drug search</Button>

        {hasPackages && <Button size={'large'} variant={'contained'}
                                data-test={'no_package_drug_button'}
                                onClick={handleSwitchClick}>
          Add {isGeneric ? 'branded' : 'generic'} drug
        </Button>}
      </Box>

    </Box>
  );
}

const BrandedDrugSelect = ({items, value, onChange}: { value: string, onChange: (value: string) => void, items: {rxcui: string, name: string}[]}) => (
  <SelectInput
    fullWidth
    hideTick
    label={
      'You can instead select from the following branded drugs:'
    }
    value={value}
    onChange={(event) => {
      onChange(event.target.value as string);
    }}
  >
    {items.map((drug) => (
      <MenuItem
        key={drug.rxcui}
        value={drug.rxcui}
        className={'regular'}
      >
        {drug.name}
      </MenuItem>
    ))}
  </SelectInput>
)
